import { useMemo } from "react"

import { Box, useTheme } from "@mui/material"

import { SKELETON_COUNT, SKELETON_INDEX_TO_REF } from "constant"
import { getIcon } from "utils/icon"
import clsx from "clsx"
import { useTranslation } from "providers/i18n"
import { useDirectory } from "hooks"

import { PageCard, PageCardContent } from "components/card"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-h/AppNavigation"
import { EmptyData } from "components/EmptyData"
import { StoreIcon } from "components/icons"
import { VenueSelector } from "components/VenueSelector"
import {
  CategoryFilterBar,
  CategoryFilterChip,
} from "components/CategoryFilterBar/index"
import { HorizontalScrollPane } from "components/HorizontalScrollPane"
import {
  DirectoryGroupLayout,
  DirectoryListLayout,
  DirectorySkeletonCard,
} from "components/domains/directory"
import { ContentWithAside } from "layouts/kiosk-h"
import { SidebarMenu } from "components/navigation/SidebarMenu"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useElementScrollRestoration } from "hooks/scrollbar/useElementScrollRestoration"
import { ScrollableTab } from "components/ScrollableTab"

export const DirectoryKioskHPage = ({
  Layout = ContentWithAside,
  AppNavigation = DefaultAppNavigation,
}) => {
  const { t } = useTranslation()
  const {
    handleChangeSubCategory,
    changeVenueFilter,
    handleChangeParentCategory,
    parentCategoryId,
    sortedParentCategoryWithOccupant,
    filteredOccupantsSorted,
    subCategoryWithActiveParentCategory,
    subCategoryId,
    venueFilter,
    venues,
    isMultiVenueProject,
    locationVenueId,
    hasParentCategory,
    showSubCategoryFilter,
    endScrollRef,
    hasNextPage,
    hasData,
    isFetched,
    layout,
  } = useDirectory()

  const theme = useTheme()

  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.directory) || <StoreIcon />,
    [theme]
  )
  const styleOverrides = theme.components[`DirectoryPage`]?.styleOverrides || {}
  const { venueSelector } = theme.components[`AppMap`]?.styleOverrides || {}
  const {
    pageCard,
    pageCardContent,
    gridContainer,
    gridItem,
    subCategoryFilterBar,
    scrollPane,
    emptyDataWrapper,
    categoryFilterBar,
    filterSection,
    levelHeader,
    divider,
  } = styleOverrides

  const isGroupLayout = layout === "group"

  usePageSideBarLayout({
    sidebar: {
      open: false,
    },
  })

  const { scrollRef } = useElementScrollRestoration<HTMLDivElement>(
    "directory-page-scroll-pane"
  )

  return (
    <Layout
      aside={
        <PageCard>
          <SidebarMenu AppNavigation={AppNavigation} />
        </PageCard>
      }
    >
      <PageCard sx={pageCard}>
        <PageCardContent sx={pageCardContent}>
          <Box
            ref={scrollRef}
            sx={{
              ...gridContainer,
              gridAutoFlow: isGroupLayout ? "" : "dense",
            }}
            className={clsx(
              hasParentCategory || hasData ? "" : "emptyList",
              showSubCategoryFilter ? "with-sub-category" : ""
            )}
          >
            {hasData ? (
              <>
                {isGroupLayout ? (
                  <DirectoryGroupLayout
                    pages={filteredOccupantsSorted.pages}
                    gridItemSx={gridItem}
                    levelHeaderSx={levelHeader}
                    dividerSx={divider}
                  />
                ) : (
                  <DirectoryListLayout
                    pages={filteredOccupantsSorted.pages}
                    gridItemSx={gridItem}
                  />
                )}
                {hasNextPage &&
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <DirectorySkeletonCard
                      ref={
                        index === SKELETON_INDEX_TO_REF ? endScrollRef : null
                      }
                      key={index}
                      sx={gridItem}
                      variant="vertical"
                    />
                  ))}
              </>
            ) : !isFetched ? (
              Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                <DirectorySkeletonCard
                  variant="vertical"
                  key={index}
                  sx={gridItem}
                />
              ))
            ) : (
              <Box sx={emptyDataWrapper}>
                <EmptyData
                  icon={emptyIcon}
                  className={showSubCategoryFilter ? "with-sub-category" : ""}
                  primary={t("Coming soon")}
                  secondary={t("Stay tuned for the update")}
                />
              </Box>
            )}
          </Box>
          <Box sx={filterSection}>
            {isMultiVenueProject && (
              <Box sx={venueSelector}>
                <VenueSelector
                  selectedVenue={venueFilter}
                  venues={venues}
                  onClickVenue={changeVenueFilter}
                  currentVenue={locationVenueId}
                />
              </Box>
            )}
            <ScrollableTab
              sx={{ visibility: hasParentCategory ? "visible" : "hidden" }}
            >
              <>
                <CategoryFilterBar
                  sx={categoryFilterBar}
                  activeCategory={parentCategoryId}
                  categories={sortedParentCategoryWithOccupant}
                  onChange={handleChangeParentCategory}
                />
              </>
            </ScrollableTab>
            {showSubCategoryFilter && (
              <HorizontalScrollPane
                className="subCategory-filterbar"
                stackProps={{
                  sx: scrollPane,
                }}
              >
                <CategoryFilterBar
                  sx={subCategoryFilterBar}
                  activeCategory={subCategoryId}
                  categories={subCategoryWithActiveParentCategory}
                  onChange={handleChangeSubCategory}
                  renderFilterButton={CategoryFilterChip}
                  enableSeparateBorder={false}
                />
              </HorizontalScrollPane>
            )}
          </Box>
        </PageCardContent>
      </PageCard>
    </Layout>
  )
}
